import { memo } from 'react';
import { LAYOUT } from './layout';
import { SLD_COLORS } from '../utils/sldTheme';
import type { SLDDeviceState } from '../utils/sldTypes';
import type { DeviceAlarmSeverity } from '../utils/deviceAlarmSeverity';

interface BESSCardProps {
  x: number;
  y: number;         // center Y of the row
  label: string;
  activePowerKw: number | null;
  stateOfCharge: number | null;
  mode: string;
  status: string;
  deviceState?: SLDDeviceState;
  /**
   * Highest ACTIVE (uncleared AND unacknowledged) alarm severity for this
   * device, or null when nothing is active. The ONLY input that raises the
   * card's fault/warning badge — the telemetry `faulted` flag deliberately
   * does not, because it stays true for acked-and-open alarms. See
   * djin-cloud#684 and utils/deviceAlarmSeverity.ts.
   */
  alarmSeverity?: DeviceAlarmSeverity | null;
  onClick: () => void;
}

function BESSCardInner({
  x, y, label, activePowerKw, stateOfCharge, mode, status, deviceState, alarmSeverity, onClick,
}: BESSCardProps) {
  const w = LAYOUT.cardW;
  const h = LAYOUT.cardH;
  const cardY = y - h / 2;

  const rawMode = (mode ?? status ?? '').toLowerCase();
  // Strict deviceState check — running-with-fault is handled separately below
  // so the operator can see what the block is doing while still being warned
  // about the fault. See spec
  // docs/plans/2026-05-21-sld-bess-fault-and-turner-collapse-design.md §4a.
  const isFault = deviceState === 'fault';
  const isOffline = deviceState === 'offline';
  const isNoComms = deviceState === 'no_comms';
  const isUnknown = deviceState === 'unknown';
  const suppressNumerics = isFault || isOffline || isNoComms || isUnknown;

  // Mode-driven inner state (used when deviceState is 'running' or undefined)
  const isCharging = !suppressNumerics &&
    (rawMode === 'charging' || rawMode === 'charge' ||
     (activePowerKw != null && activePowerKw < -1));
  const isDischarging = !suppressNumerics && !isCharging &&
    (rawMode === 'discharging' || rawMode === 'discharge' ||
     (activePowerKw != null && activePowerKw > 1));

  // Body palette
  let bodyFill: string;
  let bodyStroke: string;
  if (isFault) {
    bodyFill = SLD_COLORS.bessFault.fill;
    bodyStroke = SLD_COLORS.bessFault.stroke;
  } else if (isNoComms) {
    bodyFill = SLD_COLORS.stale.fill;
    bodyStroke = SLD_COLORS.stale.stroke;
  } else if (isOffline || isUnknown) {
    bodyFill = '#1e1e2e';
    bodyStroke = '#64748b';
  } else if (isCharging) {
    bodyFill = SLD_COLORS.bessCharging.fill;
    bodyStroke = SLD_COLORS.bessCharging.stroke;
  } else if (isDischarging) {
    bodyFill = SLD_COLORS.bessDischarging.fill;
    bodyStroke = SLD_COLORS.bessDischarging.stroke;
  } else {
    bodyFill = SLD_COLORS.bessIdle.fill;
    bodyStroke = SLD_COLORS.bessIdle.stroke;
  }

  // Status dot
  const dotColor = isFault || isOffline ? '#fb7185'
    : isNoComms ? '#fbbf24'
    : isUnknown ? '#475569'
    : isCharging ? '#38bdf8'
    : isDischarging ? '#f97316'
    : '#64748b';

  // State label
  let stateLabel: string;
  let stateLabelColor: string;
  if (isFault) {
    stateLabel = 'FAULT'; stateLabelColor = '#fb7185';
  } else if (isOffline) {
    stateLabel = 'OFFLINE'; stateLabelColor = '#fb7185';
  } else if (isNoComms) {
    stateLabel = 'NO COMMS'; stateLabelColor = '#fbbf24';
  } else if (isUnknown) {
    stateLabel = 'awaiting data'; stateLabelColor = '#64748b';
  } else if (isCharging) {
    stateLabel = 'CHARGING'; stateLabelColor = '#38bdf8';
  } else if (isDischarging) {
    stateLabel = 'DISCHARGING'; stateLabelColor = '#f97316';
  } else {
    stateLabel = 'IDLE'; stateLabelColor = '#64748b';
  }

  // Active-alarm overlay: keep the operating color + label but suffix
  // " · FAULT" (critical) or " · WARNING" (warning) in the severity color, so
  // the operator sees DISCHARGING · FAULT / CHARGING · WARNING instead of a
  // bare red FAULT. Suppressed in the offline / no-comms / unknown tiers,
  // where the comms problem is the more actionable statement, and in the
  // fault tier, which is already fully red.
  const overlaySeverity =
    !isFault && !isOffline && !isNoComms && !isUnknown ? alarmSeverity ?? null : null;
  const showFaultOverlay = overlaySeverity === 'critical';
  const showWarningOverlay = overlaySeverity === 'warning';
  // Running-but-impaired (djin-cloud#618) — LAST in precedence behind the two
  // alarm-driven overlays. It is the least specific of the three and the only
  // one that fires with no cloud alarm row at all (a sibling device offline
  // raises it), which is the case #618 exists to surface. `suppressNumerics`
  // deliberately does NOT include this tier — the block is cycling, and
  // blanking its kW/SOC would hide the very fact that it is still doing work.
  const isDegraded = deviceState === 'degraded';
  const showDegradedOverlay = isDegraded && !showFaultOverlay && !showWarningOverlay;

  // Power + SOC display
  const power = activePowerKw != null ? Math.round(activePowerKw) : 0;
  const powerStr = suppressNumerics ? '--'
    : isCharging ? `${power} kW`
    : isDischarging ? `+${Math.abs(power)} kW`
    : '0 kW';

  const soc = stateOfCharge ?? 0;
  const socStr = suppressNumerics ? '--' : `${soc.toFixed(1)}%`;

  // SOC bar dimensions (suppressed → 0-fill)
  const barX = x + 14;
  const barY = cardY + 60;
  const barW = w - 28;
  const barH = 5;
  const fillW = suppressNumerics ? 0 : Math.max(0, Math.min(barW * (soc / 100), barW));

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <rect
        x={x} y={cardY} width={w} height={h} rx={LAYOUT.cardRx}
        fill={bodyFill} stroke={bodyStroke} strokeWidth={1.2} opacity={0.95}
      />
      <circle cx={x + 16} cy={cardY + 22} r={5} fill={dotColor} />
      {isFault && <circle cx={x + 16} cy={cardY + 22} r={5} fill={dotColor} className="sld-pulse" />}
      <text x={x + 28} y={cardY + 26} fill="#e2e8f0" fontSize="14" fontWeight="700" fontFamily="monospace">
        {label}
      </text>
      <text x={x + 16} y={cardY + 46} fill={stateLabelColor} fontSize="11" fontWeight={isFault || isOffline || isNoComms ? 700 : 400} letterSpacing="0.5">
        {stateLabel}
        {showFaultOverlay && (
          <tspan fill="#fb7185" fontWeight={700}> · FAULT</tspan>
        )}
        {showWarningOverlay && (
          <tspan fill="#fbbf24" fontWeight={700}> · WARNING</tspan>
        )}
        {showDegradedOverlay && (
          <tspan fill="#fbbf24" fontWeight={700}> · DEGRADED</tspan>
        )}
      </text>
      <rect x={barX} y={barY} width={barW} height={barH} rx={2} fill="#1e293b" />
      {fillW > 0 && (
        <rect x={barX} y={barY} width={fillW} height={barH} rx={2} fill="#38bdf8" />
      )}
      <text x={x + 16} y={cardY + 84} fill="#e2e8f0" fontSize="12" fontWeight="500" fontFamily="monospace">
        {powerStr}
      </text>
      <text x={x + w - 16} y={cardY + 84} textAnchor="end" fill="#e2e8f0" fontSize="12" fontWeight="500" fontFamily="monospace">
        {socStr}
      </text>
      {showFaultOverlay && (
        <circle
          cx={x + w - 12}
          cy={cardY + 14}
          r={5}
          fill="#fb7185"
          className="sld-pulse"
          data-testid="bess-fault-overlay-badge"
        />
      )}
      {/* Amber badge: same corner, deliberately NOT pulsing — the pulse is
          reserved for critical so the two read differently at a glance.
          WARNING and DEGRADED share the slot; the label distinguishes them. */}
      {showWarningOverlay && (
        <circle
          cx={x + w - 12}
          cy={cardY + 14}
          r={5}
          fill="#fbbf24"
          data-testid="bess-warning-overlay-badge"
        />
      )}
      {showDegradedOverlay && (
        <circle
          cx={x + w - 12}
          cy={cardY + 14}
          r={5}
          fill="#fbbf24"
          data-testid="bess-degraded-overlay-badge"
        />
      )}
    </g>
  );
}

export const BESSCard = memo(BESSCardInner);
